import * as THREE from 'three';
import { PICK_MATERIAL } from '../core/materials.js';
import { FLEET, MARKET } from '../config.js';

// ---------------------------------------------------------------------------
// Other people's boats.
//
// Strictly an extra. If the socket never opens, closes, or the server is not
// there at all, the site behaves exactly as it does single-handed — nothing
// here is allowed to throw into the render loop or block the boot.
// ---------------------------------------------------------------------------

const SEND_HZ = 10;

/** Never let a price off the wire turn a catch into a debt. */
function clampPrice(p) {
    return Number.isFinite(p) ? Math.min(1000, Math.max(0.1, p)) : MARKET.price;
}

export class Fleet {
    /**
     * @param scene       where to hang other boats
     * @param makeBoat    (flagColour) => { group } — one tjalk, flying that flag
     * @param own         the local Sailing instance
     */
    constructor(scene, makeBoat, own, schools = null, onFlag = null) {
        this.scene = scene;
        this.makeBoat = makeBoat;
        this.own = own;
        // The relay owns the herring while it is up; see world/herring.js.
        this.schools = schools;
        // Told which colour we fly, once the relay has decided.
        this.onFlag = onFlag;
        // The crossing, once it exists — the relay owns the queues.
        this.crossing = null;
        // The goods trade: the relay owns the stock and both prices.
        this.trading = null;
        this.boats = new Map();     // id -> { group, target, current }
        this.ws = null;
        this.sendTimer = 0;
        this.connected = false;
        this.closing = false;       // true only when the page is going away
        this.retry = 0;
        this.skew = null;           // server clock minus ours, in ms
        this.price = MARKET.price;  // what herring fetches, relay-wide
        // Set by the game once it exists: how many herring we have aboard.
        this.catchOf = () => 0;

        if (!FLEET.enabled) return;
        try {
            this._connect();
        } catch {
            // No socket, no fleet, no problem.
        }
    }

    _url() {
        if (FLEET.url) return FLEET.url;
        const { protocol, hostname, host } = window.location;
        // On a dev machine the relay is a separate process on its own port;
        // in production Caddy proxies it on the site's own host.
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return `ws://${hostname}:${FLEET.devPort}`;
        }
        return `${protocol === 'https:' ? 'wss' : 'ws'}://${host}${FLEET.path}`;
    }

    /** Reconnect after a pause that grows with each failure, up to 8s. */
    _later() {
        if (this.closing) return;
        const wait = Math.min(8000, 700 * 2 ** this.retry++);
        setTimeout(() => this._now(), wait);
    }

    _now() {
        if (this.closing) return;
        try {
            this._connect();
        } catch {
            this._later();
        }
    }

    _connect() {
        const ws = new WebSocket(this._url());
        this.ws = ws;

        ws.onopen = () => { this.connected = true; this.retry = 0; };
        ws.onclose = () => {
            this.connected = false;
            this._clear();
            // Relay gone: go back to making our own fish rather than sailing
            // an empty sea until it comes back.
            if (this.schools) this.schools.served = false;
            this._later();
        };
        ws.onerror = () => { this.connected = false; };
        ws.onmessage = (e) => {
            let m;
            try { m = JSON.parse(e.data); } catch { return; }
            this._handle(m);
        };

        // A closing tab should free its boat at once rather than waiting to be
        // reaped. pagehide fires where beforeunload does not, notably on iOS.
        window.addEventListener('pagehide', () => {
            this.closing = true;
            try { ws.close(); } catch { /* already gone */ }
        });

        // A backgrounded tab stops animating, so it stops sending, so the
        // relay sweeps its boat up. Coming back to the front, check we are
        // still on the water and get back on it if not.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.sendTimer = 1;   // report in immediately
            } else {
                this._now();
            }
        });
    }

    _handle(m) {
        switch (m.t) {
            case 'welcome':
            case 'full':
                this.me = m.you ?? null;
                if (m.flag != null && m.flag !== this.myFlag) this.onFlag?.(m.flag);
                this.myFlag = m.flag ?? null;
                // The clock belongs to the server, so two visitors who opened
                // the page ten minutes apart share a sunset.
                if (typeof m.now === 'number') this.skew = m.now - Date.now();
                if (typeof m.price === 'number') this.price = clampPrice(m.price);
                this.crossing?.setQueues(m.queues);
                this.trading?.setMarket(m.market);
                // A welcome is the whole truth: drop anything we still hold,
                // or a re-admission leaves ghosts of boats that have gone.
                this._clear();
                for (const b of m.fleet || []) if (b.id !== this.me) this._add(b);
                this.schools?.adoptAll(m.schools);
                break;
            case 'queue':
                this.crossing?.setQueues(m.queues);
                this.trading?.setMarket(m.market);
                break;
            case 'aboard':
                this.crossing?.setAboard(m.count | 0, m.from);
                break;
            case 'price':
                if (typeof m.price === 'number') this.price = clampPrice(m.price);
                break;
            case 'school':
                this.schools?.adopt(m.school);
                break;
            case 'left':
                this.schools?.setLeft(m.id, m.left);
                break;
            case 'gone':
                this.schools?.removeById(m.id);
                break;
            case 'join':
                if (m.boat.id !== this.me) this._add(m.boat);
                break;
            case 'at': {
                const boat = this.boats.get(m.id);
                if (!boat) break;
                boat.target.set(m.x, boat.group.position.y, m.z);
                boat.targetH = m.h;
                boat.catch = m.f | 0;
                break;
            }
            case 'leave':
                this._remove(m.id);
                break;
        }
    }

    _add(info) {
        if (this.boats.has(info.id)) return;
        const built = this.makeBoat(info.flag);
        built.group.position.set(info.x, this.own.position.y, info.z);
        built.group.rotation.y = info.h;
        // An invisible box so the pointer can find them; the tjalk itself is
        // too thin a target to hit reliably at any distance.
        const pick = new THREE.Mesh(
            new THREE.BoxGeometry(9, 7, 5),
            PICK_MATERIAL,
        );
        pick.position.y = 2.5;
        pick.userData.fleetBoat = info.id;
        built.group.add(pick);

        this.scene.add(built.group);
        this.boats.set(info.id, {
            group: built.group,
            flag: info.flag,
            pick,
            target: built.group.position.clone(),
            targetH: info.h,
            catch: info.f | 0,
        });
    }

    _remove(id) {
        const boat = this.boats.get(id);
        if (!boat) return;
        this.scene.remove(boat.group);
        boat.group.traverse((o) => o.geometry?.dispose());
        this.boats.delete(id);
    }

    /**
     * Everyone on the water, own boat included, best catch first — the shape
     * the scoreboard wants.
     */
    scoreboard(ownPurse, ownHold = 0) {
        if (!this.connected || this.myFlag === null) return null;
        const rows = [{ flag: this.myFlag, count: ownPurse | 0, hold: ownHold, you: true }];
        for (const b of this.boats.values()) {
            rows.push({ flag: b.flag, count: b.catch | 0, hold: 0, you: false });
        }
        return rows.sort((a, b) => b.count - a.count);
    }

    /** Ask the relay for one fish out of a shared school. */
    take(schoolId) {
        if (!this.connected || this.ws?.readyState !== WebSocket.OPEN) return;
        if (schoolId < 0) return;   // one of ours, invented offline
        try {
            this.ws.send(JSON.stringify({ t: 'take', id: schoolId }));
        } catch { /* the socket went away mid-haul */ }
    }

    /** Buy a lot at a mill wharf. */
    buy(good, n) {
        this._say({ t: 'buy', good, n: n | 0 });
    }

    /** Land the cargo at Volendam. */
    unload(good, n) {
        this._say({ t: 'unload', good, n: n | 0 });
    }

    /** Ask the relay for whoever is waiting on a quay. */
    board(port) {
        this._say({ t: 'board', port });
    }

    /** Put them ashore. The relay checks it is not where they got on. */
    land(port) {
        this._say({ t: 'land', port });
    }

    _say(msg) {
        if (!this.connected || this.ws?.readyState !== WebSocket.OPEN) return;
        try {
            this.ws.send(JSON.stringify(msg));
        } catch { /* the socket went away */ }
    }

    /** Tell the relay a catch has been landed, which moves the price. */
    sold(n) {
        if (!this.connected || this.ws?.readyState !== WebSocket.OPEN) return;
        try {
            this.ws.send(JSON.stringify({ t: 'sold', n: n | 0 }));
        } catch { /* the socket went away */ }
    }

    /** Pick volumes for every visible boat, for the pointer test. */
    pickTargets() {
        return [...this.boats.values()].map((b) => b.pick);
    }

    /** @returns {number} what this boat has caught, or 0 */
    catchFor(id) {
        return this.boats.get(id)?.catch || 0;
    }

    /** The server's idea of now, or ours if it has not said. */
    serverNow() {
        return this.skew === null ? null : Date.now() + this.skew;
    }

    /** Every other boat's hull, for the sailing model to keep clear of. */
    hulls(radius = 3.0) {
        const out = [];
        for (const b of this.boats.values()) {
            out.push({ x: b.group.position.x, z: b.group.position.z, r: radius });
        }
        return out;
    }

    /** Where to hang that boat's label. */
    positionOf(id) {
        const boat = this.boats.get(id);
        return boat ? boat.group.position.clone().setY(boat.group.position.y + 5) : null;
    }

    _clear() {
        for (const id of [...this.boats.keys()]) this._remove(id);
    }

    update(dt) {
        // Remote boats are never simulated, only followed: their physics
        // happen on someone else's machine and guessing would only disagree.
        for (const boat of this.boats.values()) {
            boat.group.position.lerp(boat.target, Math.min(1, dt * 6));
            const d = Math.atan2(
                Math.sin(boat.targetH - boat.group.rotation.y),
                Math.cos(boat.targetH - boat.group.rotation.y),
            );
            boat.group.rotation.y += d * Math.min(1, dt * 6);
        }

        if (!this.connected || this.ws?.readyState !== WebSocket.OPEN) return;
        this.sendTimer += dt;
        if (this.sendTimer < 1 / SEND_HZ) return;
        this.sendTimer = 0;
        try {
            this.ws.send(JSON.stringify({
                x: +this.own.position.x.toFixed(2),
                z: +this.own.position.z.toFixed(2),
                h: +this.own.heading.toFixed(3),
                f: this.catchOf() | 0,
            }));
        } catch { /* the socket went away mid-frame */ }
    }
}
